import React, { useState, useEffect } from 'react';
import {
  Box, Chip, FormControl, Grid, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import { getApartments, getAuditLogs, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockAuditLogs } from '../services/mockData';
import { useAuth } from '../context/AuthContext';

const actionColors = {
  Created: 'success',
  Updated: 'warning',
  Deleted: 'error'
};

export default function AuditLogs() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [logs, setLogs] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (USE_MOCK_DATA) {
      // SuperAdmin sees all apartments, others see only theirs
      const userApartments = isSuperAdmin() ? mockApartments : [mockApartments[0]];
      setApartments(userApartments);
      setSelectedApartment(userApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    if (USE_MOCK_DATA) {
      // Filter mock logs by month/year
      const filtered = mockAuditLogs.filter(log => {
        const d = new Date(log.performedAt);
        return d.getMonth() + 1 === month && d.getFullYear() === year;
      });
      setLogs(filtered);
      return;
    }
    const fromDate = new Date(year, month - 1, 1).toISOString();
    const toDate = new Date(year, month, 0, 23, 59, 59).toISOString();
    getAuditLogs(selectedApartment, { fromDate, toDate })
      .then(res => setLogs(res.data))
      .catch(() => {});
  }, [selectedApartment, month, year]);

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const formatJson = (jsonStr) => {
    if (!jsonStr) return 'N/A';
    try {
      const obj = JSON.parse(jsonStr);
      return Object.entries(obj).map(([key, val]) => `${key}: ${val}`).join('\n');
    } catch {
      return jsonStr;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Activity Log</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {apartments.length > 1 && (
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Apartment</InputLabel>
              <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
                {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={6} sm={apartments.length > 1 ? 4 : 6}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={apartments.length > 1 ? 4 : 6}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => (
                <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>No activity logs for this month</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map(log => (
                <TableRow key={log.id}>
                  <TableCell>{new Date(log.performedAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={log.action} size="small" color={actionColors[log.action] || 'default'} />
                  </TableCell>
                  <TableCell>{log.entityType}</TableCell>
                  <TableCell>{log.description}</TableCell>
                  <TableCell>{log.performedByUser}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleViewDetails(log)}>View</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Activity Detail</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>Action:</strong> {selectedLog.action}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>Type:</strong> {selectedLog.entityType}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>Description:</strong> {selectedLog.description}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>By:</strong> {selectedLog.performedByUser}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}><strong>Date:</strong> {new Date(selectedLog.performedAt).toLocaleString()}</Typography>

              {selectedLog.oldValues && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="error">Old Values:</Typography>
                  <Paper sx={{ p: 1, bgcolor: '#fff5f5' }}>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                      {formatJson(selectedLog.oldValues)}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedLog.newValues && (
                <Box>
                  <Typography variant="subtitle2" color="success.main">New Values:</Typography>
                  <Paper sx={{ p: 1, bgcolor: '#f0fff4' }}>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                      {formatJson(selectedLog.newValues)}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
